import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { api } from "../api";
import type {
  AnalyticsDay,
  AnalyticsPage,
  CohortStats,
  CohortsPage,
  MessagePercentiles,
  FirstRequestsPage,
  RequestExamplesPage,
  StageSplit,
  TaxonomyEntry,
} from "../types";

/**
 * Acquisition + engagement reporting.
 *
 * Every number here is read from the engine's materialized `analytics_daily`
 * table — this view does no aggregation of its own, so what an operator sees is
 * exactly what the stored row says. Three things about the data shape this UI,
 * and all three are deliberate:
 *
 *  * **The funnel has three stages and only the last one is a user.** Submitted
 *    is an application, access granted is a seat, activated is someone who
 *    actually texted atula. The definitions come down with the data
 *    (`row.definitions`) and are rendered verbatim, so this page can never drift
 *    from the engine's meaning of its own columns.
 *  * **Real and self-reported source are shown side by side and never
 *    reconciled.** They disagree — people who arrive on a Meta-tagged link say
 *    LinkedIn — and that disagreement is information, not an error to correct.
 *  * **A cohort's first week is not final until it closes.** Rows carry
 *    `windows_complete`; the first-week distributions are labelled partial and
 *    excluded from the headline tiles while open, because a two-day-old cohort
 *    showing "1 active day" is an artefact of the calendar, not behaviour.
 *
 * Read-only. The table is rebuilt by `scripts/analytics_daily.py` (cron), not by
 * opening this page — a page load that triggered a full recompute over every
 * account and submission would make the cost of looking unbounded.
 */

/** The funnel's three series, in funnel order. Colours are CSS tokens validated
 *  against both surfaces (see the Analytics block in styles.css); identity is
 *  carried by the legend and the end-of-line labels, never by colour alone. */
const SERIES = [
  { key: "submitted" as const, label: "Submitted", short: "submitted", color: "var(--s-submitted)" },
  { key: "access_granted" as const, label: "Access granted", short: "granted", color: "var(--s-granted)" },
  { key: "activated" as const, label: "Activated (new users)", short: "activated", color: "var(--s-activated)" },
];

/**
 * Push end-of-line labels apart so they stay readable.
 *
 * Granted and activated track each other by nature, so their labels land within
 * a few pixels most days — on 2026-10-22 they were 9.1px apart at 11px type.
 * Nudging the labels (never the marks) keeps identity legible while the line
 * itself stays honest.
 */
function spreadLabels(
  points: { key: string; y: number }[],
  bounds: { top: number; bottom: number },
  minGap = 13,
): Record<string, number> {
  const sorted = points.map((p) => ({ ...p })).sort((a, b) => a.y - b.y);
  // Push down to open the gaps.
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].y - sorted[i - 1].y < minGap) {
      sorted[i].y = sorted[i - 1].y + minGap;
    }
  }
  // Pushing down overflows whenever the series are bunched near the baseline —
  // on a day where all three are zero they all start at it. Shift the whole
  // group back inside, then re-open gaps upward against the top bound.
  const overflow = sorted[sorted.length - 1].y - bounds.bottom;
  if (overflow > 0) for (const p of sorted) p.y -= overflow;
  for (let i = sorted.length - 2; i >= 0; i--) {
    if (sorted[i + 1].y - sorted[i].y < minGap) {
      sorted[i].y = sorted[i + 1].y - minGap;
    }
  }
  const lift = bounds.top - sorted[0].y;
  if (lift > 0) for (const p of sorted) p.y += lift;
  return Object.fromEntries(sorted.map((p) => [p.key, p.y]));
}

/** Ordered buckets, so a distribution reads left-to-right even when a bucket is
 *  empty on a given day. Must match MESSAGE_BUCKETS / SESSION_BUCKETS in the
 *  engine's storage/analytics.py. */
const MESSAGE_ORDER = ["0", "1-4", "5-9", "10-19", "20-49", "50+"];
const SESSION_ORDER = ["0", "1", "2", "3-4", "5+"];
const ACTIVE_DAYS_ORDER = ["0", "1", "2", "3", "4", "5", "6", "7"];

// Must match `request_taxonomy.NO_REQUEST`. Kept out of the request
// distribution: it is ~63% of messages and is not an ask, so including it made
// every real category look like a rounding error and made the percentages
// answer "share of messages" when the question is "share of asks".
const NO_REQUEST = "no_request";
const ASKS_NOTE =
  "Every inbound message that carries an ask — what they keep asking for. " +
  "Click a category to read them.";

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function sum(map: Record<string, number> | undefined): number {
  return Object.values(map || {}).reduce((a, b) => a + b, 0);
}

function pct(part: number, whole: number): string {
  if (!whole) return "—";
  return `${Math.round((part / whole) * 100)}%`;
}

/** Merge a per-day distribution across every row in range. */
function mergeDist(
  rows: AnalyticsDay[],
  pick: (r: AnalyticsDay) => Record<string, number> | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const row of rows) {
    for (const [k, v] of Object.entries(pick(row) || {})) out[k] = (out[k] || 0) + v;
  }
  return out;
}

/** Merge one source dimension across the range, keeping all three stages. */
function mergeSplit(
  rows: AnalyticsDay[],
  pick: (r: AnalyticsDay) => StageSplit | undefined,
): Record<string, { submitted: number; access_granted: number; activated: number }> {
  const out: Record<string, { submitted: number; access_granted: number; activated: number }> = {};
  for (const row of rows) {
    const split = pick(row);
    if (!split) continue;
    for (const stage of ["submitted", "access_granted", "activated"] as const) {
      for (const [k, v] of Object.entries(split[stage] || {})) {
        out[k] = out[k] || { submitted: 0, access_granted: 0, activated: 0 };
        out[k][stage] += v;
      }
    }
  }
  return out;
}

// ── a magnitude bar in a table cell ─────────────────────────────────────

function Bar({ value, max }: { value: number; max: number }) {
  const width = max > 0 ? Math.max(value > 0 ? 2 : 0, (value / max) * 100) : 0;
  return (
    <div className="bar-cell">
      <span className="bar-num">{value}</span>
      <div className="bar-track">
        <div className="bar-fill" style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

/** A distribution as ordered rows. `order` fixes the x-axis; any key the engine
 *  sends that is not in `order` is appended rather than dropped — a new bucket
 *  or an unrecognised goal domain must still be visible. */
function Distribution({
  title, note, dist, order, labelFor, onPick, picked, footer,
}: {
  title: string;
  note?: string;
  /** Rendered under the bars, inside the same card — for a caveat that belongs
   *  with the numbers rather than floating beneath them. */
  footer?: ReactNode;
  dist: Record<string, number>;
  order?: string[];
  /** Render a key as a human label while the KEY stays the identity. The
   *  request distributions used to be relabelled before being passed in, which
   *  threw the key away — and the key is what a click has to send. */
  labelFor?: (key: string) => string;
  onPick?: (key: string) => void;
  picked?: string;
}) {
  const keys = useMemo(() => {
    const known = (order || []).filter((k) => k in dist);
    const extra = Object.keys(dist)
      .filter((k) => !(order || []).includes(k))
      .sort((a, b) => dist[b] - dist[a]);
    return order ? [...known, ...extra] : extra;
  }, [dist, order]);
  const max = Math.max(1, ...keys.map((k) => dist[k]));
  const total = sum(dist);

  return (
    <div className="card">
      <h3 style={{ margin: "0 0 0.1rem" }}>{title}</h3>
      {note && <div className="small muted">{note}</div>}
      {total === 0 ? (
        <div className="empty muted small">No data in range.</div>
      ) : (
        <table style={{ minWidth: 0, marginTop: "0.5rem" }}>
          <tbody>
            {keys.map((k) => (
              <tr
                key={k}
                className={picked === k ? "row-picked" : undefined}
                style={onPick ? { cursor: "pointer" } : undefined}
              >
                <td className="nowrap" style={{ width: "40%" }}>
                  {onPick ? (
                    // A button, not a click handler on the row: this has to be
                    // reachable by keyboard and announced as activatable.
                    <button
                      className="linklike"
                      aria-expanded={picked === k}
                      onClick={() => onPick(k)}
                      title="Show the actual messages in this category"
                    >
                      {labelFor ? labelFor(k) : k}
                    </button>
                  ) : (
                    labelFor ? labelFor(k) : k
                  )}
                </td>
                <td><Bar value={dist[k]} max={max} /></td>
                <td className="right muted small nowrap">{pct(dist[k], total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {footer && (
        <div className="small muted" style={{ marginTop: "0.4rem" }}>{footer}</div>
      )}
    </div>
  );
}

// ── the funnel trend ────────────────────────────────────────────────────

/**
 * Three series over time. A line (not stacked bars) because the question is
 * shape — is activation tracking applications or falling behind them — and
 * because the three stages are nested, so stacking them would imply a total
 * that does not exist.
 *
 * One y-axis for all three series, by rule: they are the same measure (people)
 * at three stages, so a second scale would manufacture a crossover.
 */
function FunnelTrend({ rows }: { rows: AnalyticsDay[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 760;
  const H = 220;
  const PAD = { top: 12, right: 92, bottom: 26, left: 34 };

  if (rows.length < 2) return null;

  const max = Math.max(
    4,
    ...rows.flatMap((r) => [r.funnel.submitted, r.funnel.access_granted, r.funnel.activated]),
  );
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const x = (i: number) => PAD.left + (rows.length === 1 ? 0 : (i / (rows.length - 1)) * plotW);
  const y = (v: number) => PAD.top + plotH - (v / max) * plotH;

  // Four recessive gridlines; the axis itself carries no colour.
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));
  const active = hover === null ? rows.length - 1 : hover;
  const labelY = spreadLabels(
    SERIES.map((s) => ({ key: s.key, y: y(rows[rows.length - 1].funnel[s.key]) })),
    { top: PAD.top, bottom: PAD.top + plotH },
  );

  return (
    <div className="chart-wrap">
      <div className="chart-legend">
        {SERIES.map((s) => (
          <span className="chart-legend-item" key={s.key}>
            <span className="chart-legend-swatch" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Daily funnel: submitted, access granted and activated per day"
        onMouseLeave={() => setHover(null)}
      >
        {Array.from(new Set(ticks)).map((t) => (
          <g key={t}>
            <line
              x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)}
              stroke="var(--line)" strokeWidth="1"
            />
            <text x={PAD.left - 6} y={y(t) + 3.5} textAnchor="end"
                  fontSize="10" fill="var(--muted)">{t}</text>
          </g>
        ))}

        {/* Crosshair for the hovered day, behind the marks. */}
        <line
          x1={x(active)} x2={x(active)} y1={PAD.top} y2={PAD.top + plotH}
          stroke="var(--muted)" strokeWidth="1" strokeDasharray="2 3" opacity="0.6"
        />

        {SERIES.map((s) => (
          <polyline
            key={s.key}
            fill="none"
            stroke={s.color}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            points={rows.map((r, i) => `${x(i)},${y(r.funnel[s.key])}`).join(" ")}
          />
        ))}

        {/* The hovered day's markers get a surface ring so overlapping series
            stay separable where two lines meet. */}
        {SERIES.map((s) => (
          <circle
            key={s.key}
            cx={x(active)} cy={y(rows[active].funnel[s.key])} r="4.5"
            fill={s.color} stroke="var(--panel)" strokeWidth="2"
          />
        ))}

        {/* Direct labels at the right edge — three series, so all are labelled
            and colour is never the only cue. De-collided vertically; a leader
            line keeps a nudged label tied to its own series. */}
        {SERIES.map((s) => {
          const value = rows[rows.length - 1].funnel[s.key];
          const anchor = y(value);
          const placed = labelY[s.key];
          return (
            <g key={s.key}>
              {Math.abs(placed - anchor) > 1 && (
                <line
                  x1={W - PAD.right} x2={W - PAD.right + 6}
                  y1={anchor} y2={placed}
                  stroke={s.color} strokeWidth="1" opacity="0.5"
                />
              )}
              <text
                x={W - PAD.right + 8}
                y={placed + 3.5}
                fontSize="11"
                fill="var(--muted)"
              >
                {value} {s.short}
              </text>
            </g>
          );
        })}

        <text x={PAD.left} y={H - 8} fontSize="10" fill="var(--muted)">{rows[0].ds}</text>
        <text x={W - PAD.right} y={H - 8} textAnchor="end" fontSize="10" fill="var(--muted)">
          {rows[rows.length - 1].ds}
        </text>

        {/* Hit targets are a full column wide, not the 9px marker. */}
        {rows.map((r, i) => (
          <rect
            key={r.ds}
            x={x(i) - plotW / (rows.length * 2) - 1}
            y={PAD.top}
            width={plotW / rows.length + 2}
            height={plotH}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
          />
        ))}
      </svg>
      <div className="small muted" style={{ marginTop: "0.4rem" }}>
        <strong>{rows[active].ds}</strong>{" "}
        {SERIES.map((s) => `${s.label} ${rows[active].funnel[s.key]}`).join(" · ")}
        {" · "}activation rate {pct(rows[active].funnel.activated, rows[active].funnel.submitted)}
      </div>
    </div>
  );
}

// ── daily columns as charts ─────────────────────────────────────────────

/** Shared plot box for the day-indexed charts, so their x-axes line up. */
const DAY_CHART = { W: 760, H: 170, top: 12, right: 16, bottom: 26, left: 34 };

function dayGeometry(count: number) {
  const { W, H, top, right, bottom, left } = DAY_CHART;
  const plotW = W - left - right;
  const plotH = H - top - bottom;
  const slot = plotW / Math.max(1, count);
  return { W, H, top, right, bottom, left, plotW, plotH, slot };
}

/** Recessive gridlines + left tick labels, shared by both day charts. */
function Gridlines({ max, g, pctAxis = false }: {
  max: number;
  g: ReturnType<typeof dayGeometry>;
  pctAxis?: boolean;
}) {
  const ticks = Array.from(new Set([0, 0.5, 1].map((f) => Math.round(max * f))));
  return (
    <>
      {ticks.map((t) => {
        const y = g.top + g.plotH - (max ? (t / max) * g.plotH : 0);
        return (
          <g key={t}>
            <line x1={g.left} x2={g.W - g.right} y1={y} y2={y}
                  stroke="var(--line)" strokeWidth="1" />
            <text x={g.left - 6} y={y + 3.5} textAnchor="end"
                  fontSize="10" fill="var(--muted)">
              {pctAxis ? `${t}%` : t}
            </text>
          </g>
        );
      })}
    </>
  );
}

function DayAxis({ rows, g }: { rows: AnalyticsDay[]; g: ReturnType<typeof dayGeometry> }) {
  return (
    <>
      <text x={g.left} y={g.H - 8} fontSize="10" fill="var(--muted)">{rows[0].ds}</text>
      <text x={g.W - g.right} y={g.H - 8} textAnchor="end" fontSize="10" fill="var(--muted)">
        {rows[rows.length - 1].ds}
      </text>
    </>
  );
}

/**
 * Daily active users — anyone who sent a message that day.
 *
 * Columns, not a line: these are discrete daily counts an operator compares
 * ("was Monday bigger than Tuesday"), and a line would imply a continuous
 * quantity between days. One hue, because there is one series — the title names
 * it, so no legend box.
 */
function ActiveUsersChart({ rows }: { rows: AnalyticsDay[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const g = dayGeometry(rows.length);
  const max = Math.max(1, ...rows.map((r) => r.active_users));
  const active = hover === null ? rows.length - 1 : hover;
  const barW = Math.max(2, g.slot - 2);   // 2px surface gap between columns

  return (
    <div className="chart-wrap">
      <svg viewBox={`0 0 ${g.W} ${g.H}`} role="img"
           aria-label="Active users per day"
           onMouseLeave={() => setHover(null)}>
        <Gridlines max={max} g={g} />
        {rows.map((r, i) => {
          const h = (r.active_users / max) * g.plotH;
          const x = g.left + i * g.slot + (g.slot - barW) / 2;
          return (
            <g key={r.ds}>
              {/* 4px cap on the data end only; the baseline end stays square. */}
              <rect
                x={x} y={g.top + g.plotH - h} width={barW} height={Math.max(h, r.active_users ? 2 : 0)}
                rx="2"
                fill="var(--bar)"
                opacity={hover === null || hover === i ? 1 : 0.45}
              />
              <rect x={g.left + i * g.slot} y={g.top} width={g.slot} height={g.plotH}
                    fill="transparent" onMouseEnter={() => setHover(i)} />
            </g>
          );
        })}
        <DayAxis rows={rows} g={g} />
      </svg>
      <div className="small muted" style={{ marginTop: "0.4rem" }}>
        <strong>{rows[active].ds}</strong> · {rows[active].active_users} active user
        {rows[active].active_users === 1 ? "" : "s"}
        {" · peak "}{max} in range
      </div>
    </div>
  );
}

/**
 * Next-day retention.
 *
 * An emphasis chart, not a rate line. Each column is that day's activation
 * cohort; the filled part is the people who came back on day+1. That shows the
 * rate AND the cohort it rests on, which matters here because the cohorts are
 * tiny and volatile — "0/2" and "1/13" are not comparable percentages.
 *
 * Days with no cohort are drawn as a baseline tick, never as 0%. Four days in
 * the current range have a cohort of zero; charting those as a crash to zero
 * would invent a collapse that did not happen.
 */
function RetentionChart({ rows }: { rows: AnalyticsDay[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const g = dayGeometry(rows.length);
  const max = Math.max(1, ...rows.map((r) => r.next_day_retention.cohort));
  const barW = Math.max(2, g.slot - 2);
  // Same gate as the headline tile — a cohort counts once its day 2 has
  // finished. Anything looser double-counts today's cohort as a zero.
  const settled = rows.filter((r) => r.next_day_retention.final
                                     && r.next_day_retention.cohort > 0);
  const active = hover === null
    ? rows.indexOf(settled[settled.length - 1] ?? rows[rows.length - 1])
    : hover;
  const cur = rows[active].next_day_retention;

  const totalCohort = settled.reduce((a, r) => a + r.next_day_retention.cohort, 0);
  const totalBack = settled.reduce((a, r) => a + r.next_day_retention.returned, 0);

  return (
    <div className="chart-wrap">
      <div className="chart-legend">
        <span className="chart-legend-item">
          <span className="chart-legend-swatch" style={{ background: "var(--s-activated)" }} />
          Returned on day 2
        </span>
        <span className="chart-legend-item">
          <span className="chart-legend-swatch" style={{ background: "var(--bar-remainder)" }} />
          Did not return
        </span>
        <span className="chart-legend-item">dashed = day 2 still running</span>
        <span className="chart-legend-item">no bar = no one activated that day</span>
      </div>
      <svg viewBox={`0 0 ${g.W} ${g.H}`} role="img"
           aria-label="Next-day retention: activation cohort per day and how many returned"
           onMouseLeave={() => setHover(null)}>
        <Gridlines max={max} g={g} />
        {rows.map((r, i) => {
          const { cohort, returned, final } = r.next_day_retention;
          const x = g.left + i * g.slot + (g.slot - barW) / 2;
          const full = (cohort / max) * g.plotH;
          const back = (returned / max) * g.plotH;
          const dim = hover !== null && hover !== i ? 0.45 : 1;
          return (
            <g key={r.ds}>
              {cohort === 0 ? (
                <line
                  x1={x} x2={x + barW}
                  y1={g.top + g.plotH} y2={g.top + g.plotH}
                  stroke="var(--muted)" strokeWidth="2" opacity={0.35 * dim}
                />
              ) : !final ? (
                /* Day 2 has not finished. The cohort exists, so its height is
                   known, but its retention is not — drawn as an outline so it
                   reads as pending rather than as a cohort nobody returned to. */
                <rect
                  x={x} y={g.top + g.plotH - full} width={barW} height={full}
                  rx="2" fill="none"
                  stroke="var(--bar-remainder)" strokeWidth="1.5"
                  strokeDasharray="3 2" opacity={dim}
                />
              ) : (
                <>
                  <rect x={x} y={g.top + g.plotH - full} width={barW} height={full}
                        rx="2" fill="var(--bar-remainder)" opacity={dim} />
                  {returned > 0 && (
                    <rect x={x} y={g.top + g.plotH - back} width={barW}
                          height={Math.max(back, 2)} rx="2"
                          fill="var(--s-activated)" opacity={dim} />
                  )}
                </>
              )}
              <rect x={g.left + i * g.slot} y={g.top} width={g.slot} height={g.plotH}
                    fill="transparent" onMouseEnter={() => setHover(i)} />
            </g>
          );
        })}
        <DayAxis rows={rows} g={g} />
      </svg>
      <div className="small muted" style={{ marginTop: "0.4rem" }}>
        <strong>{rows[active].ds}</strong>{" "}
        {cur.cohort === 0
          ? "no activations — retention undefined"
          : !cur.final
            ? `${cur.cohort} activated — day 2 still running`
            : `${cur.returned} of ${cur.cohort} came back (${pct(cur.returned, cur.cohort)})`}
        {" · range "}
        {totalBack}/{totalCohort} ({pct(totalBack, totalCohort)}) over{" "}
        {settled.length} settled day{settled.length === 1 ? "" : "s"}
      </div>
    </div>
  );
}

// ── source tables ───────────────────────────────────────────────────────

function SourceTable({
  title, note, data,
}: {
  title: string;
  note: string;
  data: Record<string, { submitted: number; access_granted: number; activated: number }>;
}) {
  const keys = Object.keys(data).sort(
    (a, b) => data[b].activated - data[a].activated || data[b].submitted - data[a].submitted,
  );
  const totals = keys.reduce(
    (acc, k) => ({
      submitted: acc.submitted + data[k].submitted,
      access_granted: acc.access_granted + data[k].access_granted,
      activated: acc.activated + data[k].activated,
    }),
    { submitted: 0, access_granted: 0, activated: 0 },
  );

  return (
    <div className="card">
      <h3 style={{ margin: "0 0 0.1rem" }}>{title}</h3>
      <div className="small muted">{note}</div>
      {keys.length === 0 ? (
        <div className="empty muted small">No data in range.</div>
      ) : (
        <div className="table-scroll" style={{ marginTop: "0.5rem", border: "none" }}>
          <table style={{ minWidth: 0 }}>
            <thead>
              <tr>
                <th>Source</th>
                <th className="right">Submitted</th>
                <th className="right">Granted</th>
                <th className="right">Activated</th>
                <th className="right">Sub&nbsp;→&nbsp;act</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k}>
                  <td className="nowrap"><code>{k}</code></td>
                  <td className="right">{data[k].submitted}</td>
                  <td className="right">{data[k].access_granted}</td>
                  <td className="right">{data[k].activated}</td>
                  <td className="right muted">
                    {pct(data[k].activated, data[k].submitted)}
                  </td>
                </tr>
              ))}
              <tr>
                <td className="nowrap"><strong>All</strong></td>
                <td className="right"><strong>{totals.submitted}</strong></td>
                <td className="right"><strong>{totals.access_granted}</strong></td>
                <td className="right"><strong>{totals.activated}</strong></td>
                <td className="right muted">
                  {pct(totals.activated, totals.submitted)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── join-week cohorts ───────────────────────────────────────────────────

function Rate({ part }: { part: { eligible: number; count: number } }) {
  if (part.eligible === 0) return <span className="muted">too early</span>;
  return (
    <>
      <strong>{pct(part.count, part.eligible)}</strong>
      <span className="muted"> {part.count}/{part.eligible}</span>
    </>
  );
}

function Quartiles({ q, showUsers = false }: {
  q: MessagePercentiles | null;
  showUsers?: boolean;
}) {
  if (!q) return <span className="muted">not ready</span>;
  return (
    <>
      {q.p25} / <strong>{q.p50}</strong> / {q.p75}
      {showUsers && <span className="muted"> ({q.users})</span>}
    </>
  );
}

function CohortCells({ row }: { row: CohortStats }) {
  return (
    <>
      <td className="right">{row.joined}</td>
      <td className="right nowrap"><Rate part={row.returned} /></td>
      <td className="right nowrap"><Rate part={row.next_day} /></td>
      <td className="right nowrap"><Quartiles q={row.messages} /></td>
      <td className="right nowrap"><Quartiles q={row.first_week_messages} showUsers /></td>
    </>
  );
}

/** Who came back and how much they said, by the week they joined.
 *
 *  "Returned" keeps climbing for as long as a cohort has days left, so it favours
 *  older weeks. Next-day and first-7-days are the fixed windows, and the ones to
 *  compare across weeks.
 */
function Cohorts({
  since, until, env,
}: {
  since: string;
  until: string;
  env: string;
}) {
  const [page, setPage] = useState<CohortsPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .analyticsCohorts(since, until, env)
      .then((p) => !cancelled && setPage(p))
      .catch((e) =>
        !cancelled &&
        setError(e instanceof Error ? e.message : "Could not load cohorts."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [since, until, env]);

  return (
    <div className="card" style={{ marginTop: "1rem" }}>
      <h3 style={{ margin: "0 0 0.1rem" }}>Cohorts by join week</h3>
      <div className="small muted">
        Joined means sent their first message. Weeks run Saturday to Friday. Only
        the user's own messages count, so a nudge from atula is not a return, but a
        reply to one is, even an opt-out. Live calls are not counted. Compare weeks
        on next-day and first 7 days: "returned" keeps rising as a cohort ages.
      </div>
      {loading && <div className="empty muted small">Loading…</div>}
      {error && <div className="error">{error}</div>}
      {page && page.weeks.length === 0 && (
        <div className="empty muted small">No one joined in this range.</div>
      )}
      {page && page.weeks.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th className="nowrap">Join week</th>
                <th className="right">Joined</th>
                <th className="right nowrap" title="Sent a message on any later day. Leaves out people who joined today.">
                  Returned
                </th>
                <th className="right nowrap" title="Sent a message the day after joining. Leaves out people whose next day has not finished.">
                  Back next day
                </th>
                <th className="right nowrap" title="Messages per user, all time">
                  Messages P25 / P50 / P75
                </th>
                <th className="right nowrap" title="Only people whose 7 days have fully elapsed; how many is in brackets">
                  First 7 days P25 / P50 / P75
                </th>
              </tr>
            </thead>
            <tbody>
              {page.weeks.map((w) => (
                <tr key={w.week_start}>
                  <td className="nowrap">
                    {w.week_start.slice(5)} – {w.week_end.slice(5)}
                    {w.open && <span className="small muted"> · still open</span>}
                  </td>
                  <CohortCells row={w} />
                </tr>
              ))}
              <tr>
                <td><strong>All</strong></td>
                <CohortCells row={page.total} />
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {page && page.excluded_no_messages > 0 && (
        <div className="small muted" style={{ marginTop: "0.35rem" }}>
          {page.excluded_no_messages} activated with no messages in this
          environment, left out.
        </div>
      )}
    </div>
  );
}

// ── request categories ──────────────────────────────────────────────────

/** Every user's first ask, our label for it, and whether they stayed.
 *
 *  The distribution above says what brings people in. This says whether the
 *  thing that brought someone in is the thing that kept them — which needs the
 *  ask and the engagement on the same row, and is the question a bar chart of
 *  first requests cannot answer no matter how it is sorted.
 */
function FirstAsks({
  since, until, env,
}: {
  since: string;
  until: string;
  env: string;
}) {
  const [page, setPage] = useState<FirstRequestsPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .analyticsFirstRequests(since, until, env)
      .then((p) => !cancelled && setPage(p))
      .catch((e) =>
        !cancelled &&
        setError(e instanceof Error ? e.message : "Could not load first asks."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [since, until, env]);

  const items = page?.items || [];
  const oneAndDone = items.filter((i) => i.active_days <= 1).length;

  return (
    <div className="card" style={{ marginTop: "1rem" }}>
      <h3 style={{ margin: "0 0 0.1rem" }}>First asks</h3>
      <div className="small muted">
        What each user actually said first, how we labelled it, and how many
        days they have been active since activating.
      </div>
      {loading && <div className="empty muted small">Loading…</div>}
      {error && <div className="error">{error}</div>}
      {page && items.length === 0 && (
        <div className="empty muted small">No classified first asks in range.</div>
      )}
      {items.length > 0 && (
        <>
          <div className="small muted" style={{ margin: "0.4rem 0" }}>
            {items.length} user{items.length === 1 ? "" : "s"}
            {" · "}
            <strong>{oneAndDone}</strong> active on only one day
            {" "}({pct(oneAndDone, items.length)})
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">Asked</th>
                  <th>First ask</th>
                  <th className="nowrap">Our category</th>
                  <th className="right nowrap" title="Distinct days with an inbound message since activation, over calendar days elapsed">
                    Active days
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={`${it.user_id}-${i}`}>
                    <td className="nowrap small muted">{it.asked_on}</td>
                    <td>
                      {it.missing ? (
                        <span className="muted">(no longer in the thread)</span>
                      ) : (
                        it.text
                      )}
                      {it.stale && (
                        <div className="small muted">
                          ⚠ edited since it was classified
                        </div>
                      )}
                      <div className="small muted"><code>{it.user_id}</code></div>
                    </td>
                    <td className="nowrap small">{it.label}</td>
                    <td className="right nowrap">
                      {/* Both numbers, always: "1" alone cannot be read without
                          knowing whether they have had 2 days or 30. */}
                      <strong>{it.active_days}</strong>
                      <span className="muted"> / {it.days_since_activation}</span>
                      {it.activation_estimated && (
                        <div className="small muted" title="No activated_at on the account; the ask day stands in">
                          est.
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/** The messages behind one bar — what the label is actually made of.
 *
 *  A distribution says how many; it cannot say whether the label is right. This
 *  is the audit path, and it matters most for the two categories nothing else
 *  can check: `other` (a rising share is the signal to add a category) and
 *  `no_request` (which absorbs ~63% of traffic, so a mistake there is invisible
 *  in every other number on the page).
 */
function RequestExamples({
  category, label, since, until, env, firstOnly, onClose,
}: {
  category: string;
  label: string;
  since: string;
  until: string;
  env: string;
  firstOnly: boolean;
  onClose: () => void;
}) {
  const [page, setPage] = useState<RequestExamplesPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPage(null);
    api
      .analyticsRequests(category, since, until, env, firstOnly)
      .then((p) => !cancelled && setPage(p))
      .catch((e) =>
        !cancelled &&
        setError(e instanceof Error ? e.message : "Could not load examples."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [category, since, until, env, firstOnly]);

  const stale = page?.items.filter((i) => i.stale || i.missing).length || 0;

  return (
    <div className="card" style={{ marginTop: "0.6rem" }}>
      <div className="section-head">
        <h3 style={{ margin: 0 }}>
          {label}
          {firstOnly && <span className="muted"> — first requests only</span>}
        </h3>
        <button className="navlink" onClick={onClose}>Close</button>
      </div>
      {page?.definition && (
        <div className="small muted" style={{ marginBottom: "0.4rem" }}>
          {page.definition}
        </div>
      )}
      {loading && <div className="empty muted small">Loading…</div>}
      {error && <div className="error">{error}</div>}
      {page && page.items.length === 0 && (
        <div className="empty muted small">No messages in this range.</div>
      )}
      {page && page.items.length > 0 && (
        <>
          <div className="small muted" style={{ marginBottom: "0.4rem" }}>
            {page.items.length} most recent
            {stale > 0 && (
              <span className="badge warn" style={{ marginLeft: "0.4rem" }}>
                {stale} no longer match the labelled text
              </span>
            )}
          </div>
          <div className="table-scroll">
            <table>
              <tbody>
                {page.items.map((it, i) => (
                  <tr key={`${it.user_id}-${it.ds}-${i}`}>
                    <td className="nowrap small muted" style={{ width: "1%" }}>
                      {it.ds}
                      {it.is_first && (
                        <div><span className="badge warn">first</span></div>
                      )}
                    </td>
                    <td>
                      {it.missing ? (
                        <span className="muted">
                          (message no longer in the thread)
                        </span>
                      ) : (
                        <>
                          {it.text}
                          {it.stale && (
                            <div className="small muted">
                              ⚠ edited since it was classified — the label may
                              describe different words
                            </div>
                          )}
                        </>
                      )}
                      <div className="small muted">
                        <code>{it.user_id}</code>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function RequestCategories({
  rows, taxonomy, version, since, until, env,
}: {
  rows: AnalyticsDay[];
  taxonomy: TaxonomyEntry[];
  version: string;
  since: string;
  until: string;
  env: string;
}) {
  // Which bar is open, per panel — the two distributions answer different
  // questions, so opening one must not close the other.
  const [pickedAll, setPickedAll] = useState("");
  const [showLegend, setShowLegend] = useState(false);
  // A day the classifier has not reached omits the field entirely, so an
  // unclassified range and a silent one are distinguishable.
  const classified = rows.filter((r) => r.first_request !== undefined);
  const all = mergeDist(classified, (r) => r.all_requests);
  // `no_request` is most of the traffic and is not an ask, so it is kept OUT of
  // the distribution and reported as a count beside it. Leaving it in made every
  // real category look like a rounding error and made the percentages answer
  // "share of messages" when the question is "share of asks".
  const noAsk = all[NO_REQUEST] || 0;
  const asks = Object.fromEntries(
    Object.entries(all).filter(([k]) => k !== NO_REQUEST),
  );
  const labels = new Map(taxonomy.map((t) => [t.key, t.label]));

  if (classified.length === 0) {
    return (
      <div className="card">
        <h3 style={{ margin: 0 }}>Request categories</h3>
        <div className="empty muted small">
          Not classified for this range yet — run
          {" "}<code>scripts/classify_requests.py --since {rows[0]?.ds || "<day>"}</code>.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="section-head">
        <h2 style={{ margin: 0 }}>Request categories</h2>
        <button className="navlink" onClick={() => setShowLegend((v) => !v)}>
          {showLegend ? "Hide definitions" : "What do these mean?"}
        </button>
      </div>
      <div className="small muted" style={{ marginBottom: "0.5rem" }}>
        Taxonomy <code>{version}</code> · {classified.length} of {rows.length} day(s)
        classified
      </div>
      {showLegend && (
        <div className="card">
          <dl className="kv">
            {taxonomy.map((t) => (
              <div key={t.key} style={{ display: "contents" }}>
                <dt className="nowrap"><code>{t.key}</code></dt>
                <dd>
                  <strong>{t.label}</strong> — {t.definition}
                  {t.examples && t.examples !== "—" && (
                    <div className="small muted">{t.examples}</div>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      <Distribution
        title="All requests"
        note={ASKS_NOTE}
        dist={asks}
        labelFor={(k) => labels.get(k) || k}
        onPick={(k) => setPickedAll((p) => (p === k ? "" : k))}
        picked={pickedAll}
        footer={
          <>
            {noAsk} of {noAsk + sum(asks)} message(s) carried no ask at all
            (greetings, &ldquo;ready&rdquo;, scheduling, in-session logistics)
            and are excluded above. Percentages are of real asks.
          </>
        }
      />
      {pickedAll && (
        <RequestExamples
          category={pickedAll}
          label={labels.get(pickedAll) || pickedAll}
          since={since}
          until={until}
          env={env}
          firstOnly={false}
          onClose={() => setPickedAll("")}
        />
      )}
      <FirstAsks since={since} until={until} env={env} />
    </>
  );
}

// ── the view ────────────────────────────────────────────────────────────

export function Analytics() {
  const [since, setSince] = useState(isoDaysAgo(29));
  const [until, setUntil] = useState("");
  const [env, setEnv] = useState<"production" | "development">("production");
  const [includePartial, setIncludePartial] = useState(true);
  const [page, setPage] = useState<AnalyticsPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPage(await api.analyticsDaily(since, until, env));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load analytics.");
    } finally {
      setLoading(false);
    }
  }, [since, until, env]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = page?.rows || [];
  // Cohorts whose SECOND day has finished — the only gate next-day retention
  // needs. Gating it on the 7-day window (as this first did) threw away most of
  // a finished sample and disagreed with the chart below it.
  const d1Rows = rows.filter((r) => r.next_day_retention.final);
  // Cohorts still inside their week. Included in the first-week distributions by
  // default and labelled, rather than dropped — see `includePartial`.
  const partialRows = rows.filter(
    (r) => !r.windows_complete && r.funnel.activated > 0,
  );
  const closedRows = rows.filter((r) => r.windows_complete);
  // Default to the fuller picture: at this scale closed cohorts alone are a
  // handful of users, and a tiny comparable sample is less useful than a larger
  // censored one that says so.
  const firstWeekRows = includePartial ? rows : closedRows;
  // The oldest partial cohort's maturity — the ceiling on what a partial cohort
  // could possibly show, which is the number that makes "censored" concrete.
  const partialMaturity = partialRows.length
    ? Math.max(...partialRows.map((r) => r.days_observed))
    : 0;

  const totals = useMemo(() => {
    const activated = rows.reduce((a, r) => a + r.funnel.activated, 0);
    const submitted = rows.reduce((a, r) => a + r.funnel.submitted, 0);
    const granted = rows.reduce((a, r) => a + r.funnel.access_granted, 0);
    const retCohort = d1Rows.reduce((a, r) => a + r.next_day_retention.cohort, 0);
    const retBack = d1Rows.reduce((a, r) => a + r.next_day_retention.returned, 0);
    const peakActive = Math.max(0, ...rows.map((r) => r.active_users));
    const estimated = rows.reduce((a, r) => a + r.activation_day_estimated, 0);
    return { activated, submitted, granted, retCohort, retBack, peakActive, estimated };
  }, [rows, d1Rows]);

  return (
    <section>
      <div className="section-head">
        <h2 style={{ margin: 0 }}>Analytics</h2>
        <div className="tabs" style={{ margin: 0 }}>
          {(["production", "development"] as const).map((e) => (
            <button
              key={e}
              className={env === e ? "tab active" : "tab"}
              onClick={() => setEnv(e)}
            >
              {e === "production" ? "Production" : "Dev"}
            </button>
          ))}
        </div>
      </div>

      {/* Filters in one row above everything they affect. */}
      <div className="add-admin">
        <label className="nowrap small muted">
          From{" "}
          <input type="date" value={since} onChange={(e) => setSince(e.target.value)} />
        </label>
        <label className="nowrap small muted">
          To{" "}
          <input
            type="date"
            value={until}
            placeholder="today"
            onChange={(e) => setUntil(e.target.value)}
          />
        </label>
        <button onClick={() => void load()} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {page?.hint && <div className="badge warn">{page.hint}</div>}

      {rows.length === 0 && !loading && !page?.hint && (
        <div className="empty muted">No rows in this range.</div>
      )}

      {rows.length > 0 && (
        <>
          {/* The definitions come from the engine so this page cannot rename a
              stage. "Activated = new user" is the one that matters most. */}
          <ul className="defs">
            {Object.entries(rows[0].definitions || {}).map(([stage, text]) => (
              <li key={stage}>
                <code>{stage}</code> — {text}
              </li>
            ))}
          </ul>

          <div className="tiles">
            <div className="tile">
              <div className="tile-label">New users</div>
              <div className="tile-value">{totals.activated}</div>
              <div className="tile-note">activated in range</div>
            </div>
            <div className="tile">
              <div className="tile-label">Submitted</div>
              <div className="tile-value">{totals.submitted}</div>
              <div className="tile-note">
                {pct(totals.activated, totals.submitted)} reach activation
              </div>
            </div>
            <div className="tile">
              <div className="tile-label">Access granted</div>
              <div className="tile-value">{totals.granted}</div>
              <div className="tile-note">
                {pct(totals.activated, totals.granted)} of seats used
              </div>
            </div>
            <div className="tile">
              <div className="tile-label">Next-day retention</div>
              <div className="tile-value">{pct(totals.retBack, totals.retCohort)}</div>
              <div className="tile-note">
                {totals.retBack} of {totals.retCohort} new users came back
              </div>
            </div>
            <div className="tile">
              <div className="tile-label">Peak active / day</div>
              <div className="tile-value">{totals.peakActive}</div>
              <div className="tile-note">anyone who messaged</div>
            </div>
          </div>

          {totals.estimated > 0 && (
            <div className="badge warn" style={{ marginBottom: "0.75rem" }}>
              {totals.estimated} activation(s) dated from first message activity because{" "}
              <code>accounts.activated_at</code> was missing.
            </div>
          )}

          <h3>Funnel by day</h3>
          <FunnelTrend rows={rows} />

          <div className="grid-2" style={{ marginTop: "1rem" }}>
            <div>
              <h3 style={{ margin: "0 0 0.1rem" }}>Active users per day</h3>
              <div className="small muted" style={{ marginBottom: "0.4rem" }}>
                Anyone who sent a message that day, whenever they activated.
              </div>
              <ActiveUsersChart rows={rows} />
            </div>
            <div>
              <h3 style={{ margin: "0 0 0.1rem" }}>Next-day retention</h3>
              <div className="small muted" style={{ marginBottom: "0.4rem" }}>
                Each column is that day's activation cohort; the filled part came
                back on day 2. Cohorts this small are volatile — read the heights,
                not just the rate.
              </div>
              <RetentionChart rows={rows} />
            </div>
          </div>

          <Cohorts since={page?.since || since} until={page?.until || until} env={page?.env || env} />

          {/* The per-day funnel table used to sit here. Every column it carried
              is already in the three charts above — funnel trend, active users
              and D1 retention — so it restated them in the one form that cannot
              show a shape.

              Its "◦ = 7-day window still open" marker is not missed, because it
              flagged the wrong thing: it keyed on `windows_complete`, but a
              day's submitted/granted/activated and active-users counts are
              final the moment that PT day ends and never move again. Only the
              first-week distributions and D1 retention depend on a window, and
              each gates itself — D1 on `next_day_retention.final`, the
              distributions on `days_observed` via `partialMaturity`. */}
          <div className="small muted" style={{ marginTop: "0.35rem" }}>
            Days bucketed in {page?.timezone}.
          </div>

          <h3 style={{ marginTop: "1.5rem" }}>Where they came from</h3>
          <div className="grid-2">
            <SourceTable
              title="Real source (web tracking)"
              note="From the landing URL's utm_source, grouped by platform. Ground truth for spend."
              data={mergeSplit(rows, (r) => r.by_real_source)}
            />
            <SourceTable
              title="Self-reported source"
              note="“Where did you first hear about atula?” on the signup form. Not reconciled against the real source — the gap is the finding."
              data={mergeSplit(rows, (r) => r.by_self_source)}
            />
          </div>
          <div className="grid-2" style={{ marginTop: "1rem" }}>
            <SourceTable
              title="Campaign"
              note="utm_campaign — the A/B dimension underneath platform."
              data={mergeSplit(rows, (r) => r.by_real_campaign)}
            />
            <Distribution
              title="Goal types"
              note="Goal domain, for goals created in range."
              dist={mergeDist(rows, (r) => r.goal_types)}
            />
          </div>

          <div className="section-head" style={{ marginTop: "1.5rem" }}>
            <h3 style={{ margin: 0 }}>First week after activation</h3>
            <button className="navlink" onClick={() => setIncludePartial((v) => !v)}>
              {includePartial ? "Closed cohorts only" : "Include cohorts still in week 1"}
            </button>
          </div>
          <div className="small muted" style={{ marginBottom: "0.5rem" }}>
            {includePartial ? (
              <>
                All {firstWeekRows.reduce((a, r) => a + r.funnel.activated, 0)} user
                {firstWeekRows.reduce((a, r) => a + r.funnel.activated, 0) === 1 ? "" : "s"}{" "}
                who activated in range, including{" "}
                {partialRows.reduce((a, r) => a + r.funnel.activated, 0)} still inside
                week 1. <strong>Those are censored, not quiet</strong> — a cohort{" "}
                {partialRows.length > 0 && <>{partialMaturity} </>}
                days in cannot show more than that many active days, so the low
                buckets are inflated.
              </>
            ) : (
              <>
                Closed cohorts only — {closedRows.length} of {rows.length} day
                {rows.length === 1 ? "" : "s"}, covering{" "}
                {closedRows.reduce((a, r) => a + r.funnel.activated, 0)} user
                {closedRows.reduce((a, r) => a + r.funnel.activated, 0) === 1 ? "" : "s"}.
                Comparable, but a small sample while you are growing.
              </>
            )}
          </div>
          <div className="grid-2">
            <Distribution
              title="Active days in first week"
              note="Days with at least one inbound message, 0–7."
              dist={mergeDist(firstWeekRows, (r) => r.first_week_active_days)}
              order={ACTIVE_DAYS_ORDER}
            />
            <Distribution
              title="Messages in first week"
              note="Inbound user messages, any channel."
              dist={mergeDist(firstWeekRows, (r) => r.first_week_messages)}
              order={MESSAGE_ORDER}
            />
            <Distribution
              title="Live sessions joined in first week"
              note="Sessions a human actually joined — not sessions created or dialled into."
              dist={mergeDist(firstWeekRows, (r) => r.first_week_live_sessions_joined)}
              order={SESSION_ORDER}
            />
          </div>

          <div style={{ marginTop: "1.5rem" }}>
            <RequestCategories
              rows={rows}
              taxonomy={page?.taxonomy || []}
              version={page?.taxonomy_version || ""}
              since={page?.since || since}
              until={page?.until || until}
              env={page?.env || env}
            />
          </div>

          {/* Point-in-time analyses live at their own URL rather than in the nav:
              they answer a question asked once. A real <a> so the link is
              copyable and opens in a new tab like any other document. */}
          <div className="small muted" style={{ marginTop: "2rem" }}>
            Deep dives:{" "}
            <a href="/report">First-ask deep dive</a> — what users asked for first,
            what atula did with it, and whether they came back.
          </div>
        </>
      )}
    </section>
  );
}
